import {useCallback, useRef} from 'react'
import {useMappedState} from 'redux-react-hook'
import {formatNumber} from 'utils/format'
import {floor} from 'utils/number'
import {unrealizedPnlFn, initialMarginNotionalFn, totalInitialMarginFn} from 'utils/futures'
import useSymbolInfo from 'hooks/useSymbolInfo'
import useLeverageData from 'hooks/useLeverageData'

function useMarginBalance (symbol) {
  const timeRef = useRef(0)
  const dataRef = useRef([])

  const {symbolInfo} = useSymbolInfo()
  const {quoteAsset, pricePrecision} = symbolInfo
  const {leverageData} = useLeverageData()
  const {curLeverage} = leverageData

  const mapState = useCallback(
    state => ({
      curPosition: state?.entities?.positions?.[symbol] || {},
      balance: state?.entities?.balances || {}
    }),
    [symbol]
  )
  const {curPosition, balance} = useMappedState(mapState)
  let calculateB = []
  if (curPosition?.symbol) {
    calculateB = Object.keys(balance).reduce((b, key) => {
      const curBalance = balance?.[key]
      let totalInitialMargin = curBalance?.initialMargin
      if (curPosition?.markPrice) {
        const initialMarginNotional = initialMarginNotionalFn(curPosition)
        totalInitialMargin = Number(totalInitialMarginFn(initialMarginNotional, curLeverage))
      }
      const unrealizedPnl = Number(unrealizedPnlFn(curPosition))
      const walletBalance = Number(curBalance?.walletBalance)
      const availableForOrder = walletBalance + unrealizedPnl - totalInitialMargin || 0
      const newBalance = {
        ...curBalance,
        initialMargin: totalInitialMargin,
        availableForOrder: Math.max(availableForOrder, 0),
        unrealizedPnl,
        availableForWithDraw: Math.max(Math.min(walletBalance, availableForOrder), 0)
      }
      b[key] = newBalance
      return b
    }, {})

    if (calculateB) {
      calculateB = Object.values(calculateB).map(v => ({
        ...v,
        walletBalance: floor(v.walletBalance, pricePrecision, true),
        unrealizedProfit: formatNumber(v.unrealizedProfit),
        initialMargin: formatNumber(v.initialMargin),
        availableForOrder: floor(v.availableForOrder, pricePrecision, true),
        freeBalance: v.availableForOrder,
        availableForWithDraw: floor(v.availableForWithDraw, pricePrecision, true),
        unrealizedPnl: formatNumber(v.unrealizedPnl)
      }))
    }
  }

  calculateB = (calculateB || []).filter(v => v.asset === quoteAsset)
  const now = Date.now()
  if (timeRef.current && now - timeRef.current < 250) {
    return dataRef.current
  }
  timeRef.current = now
  dataRef.current = calculateB

  return calculateB
}

export default useMarginBalance
